import { z } from 'zod';
import { AMOUNT_SCALE, fromScaled, toScaled } from '../../money.ts';
import type { LocalizedText, PermissionDefinition } from '../../permissions.ts';

/** Módulo Pedidos (catálogo beadev-ai/catalogo): permissões, regras, validações e tipos. */

export const PERMISSION_MODULES = {
  orders: { 'pt-BR': 'Pedidos', en: 'Orders' },
} satisfies Record<string, LocalizedText>;

export const PERMISSIONS = [
  {
    code: 'orders.order.read',
    module: 'orders',
    label: { 'pt-BR': 'Ver pedidos', en: 'View orders' },
  },
  {
    code: 'orders.order.manage',
    module: 'orders',
    label: {
      'pt-BR': 'Criar, alterar, confirmar, entregar e cancelar pedidos',
      en: 'Create, edit, confirm, deliver and cancel orders',
    },
  },
] as const satisfies readonly PermissionDefinition[];

export const ORDER_STATUSES = ['draft', 'confirmed', 'delivered', 'cancelled'] as const;
export type OrderStatus = (typeof ORDER_STATUSES)[number];

/**
 * Para onde cada situação pode ir. Só o rascunho muda de itens; entregue e cancelado são finais.
 * O banco confere a mesma regra (orders.check_order_change).
 */
export const NEXT_STATUSES: Record<OrderStatus, readonly OrderStatus[]> = {
  draft: ['confirmed', 'cancelled'],
  confirmed: ['delivered', 'cancelled'],
  delivered: [],
  cancelled: [],
};

export function canMove(from: OrderStatus, to: OrderStatus): boolean {
  return NEXT_STATUSES[from].includes(to);
}

/**
 * Texto decimal digitado (aceita vírgula: "1,5", "1.234,56") no formato da base ("1.5",
 * "1234.56"). Devolve null se não for um número.
 */
export function normalizeDecimal(value: string): string | null {
  let s = value.trim().replace(/\s/g, '');
  if (s.includes(',')) s = s.replace(/\./g, '').replace(',', '.');
  return /^\d+(\.\d{1,4})?$/.test(s) ? s : null;
}

/**
 * Valor de um item: quantidade × preço, arredondado em 4 casas para longe do zero, como o
 * `round(quantity * unit_price, 4)` do banco. Tudo em bigint, nunca em ponto flutuante.
 */
export function lineAmount(quantity: string, unitPrice: string): string {
  const factor = 10n ** BigInt(AMOUNT_SCALE);
  const product = toScaled(quantity) * toScaled(unitPrice);
  const negative = product < 0n;
  const abs = negative ? -product : product;
  const rounded = (abs + factor / 2n) / factor;
  return fromScaled(negative ? -rounded : rounded);
}

/** A soma dos valores dos itens. */
export function orderTotal(items: readonly { quantity: string; unitPrice: string }[]): string {
  return fromScaled(
    items.reduce((sum, i) => sum + toScaled(lineAmount(i.quantity, i.unitPrice)), 0n),
  );
}

/** Número do pedido para mostrar: 12 → PED-000012. */
export function orderLabel(number: number): string {
  return `PED-${String(number).padStart(6, '0')}`;
}

const decimal = (positive: boolean) =>
  z
    .string()
    .max(30)
    .transform((s, ctx) => {
      const n = normalizeDecimal(s);
      if (n === null || toScaled(n) > 999_999_999_999_999n * 10n ** 4n) {
        ctx.addIssue({ code: 'custom', message: 'Número inválido (até 4 casas decimais)' });
        return z.NEVER;
      }
      if (positive && toScaled(n) === 0n) {
        ctx.addIssue({ code: 'custom', message: 'Precisa ser maior que zero' });
        return z.NEVER;
      }
      return n;
    });

export const orderItemInput = z.object({
  description: z.string().trim().min(1).max(200),
  quantity: decimal(true),
  unitPrice: decimal(false),
});
export type OrderItemInput = z.input<typeof orderItemInput>;

export const orderInput = z.object({
  customerId: z.uuid(),
  /** Data de entrega (AAAA-MM-DD); vazio = sem data. */
  dueDate: z
    .union([z.literal(''), z.iso.date()])
    .nullable()
    .optional()
    .transform((d) => d || null),
  currency: z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z]{3}$/)
    .default('BRL'),
  notes: z
    .string()
    .trim()
    .max(2000)
    .nullable()
    .optional()
    .transform((s) => s || null),
  items: z.array(orderItemInput).min(1).max(100),
});
export type OrderInput = z.input<typeof orderInput>;

export const orderStatusInput = z.object({ status: z.enum(ORDER_STATUSES) });

export interface OrderItem {
  id: string;
  description: string;
  quantity: string;
  unitPrice: string;
  amount: string;
}

/** O pedido como a lista mostra. */
export interface OrderSummary {
  id: string;
  number: number;
  customerId: string;
  /** null se a pessoa não pode ver o cliente. */
  customerName: string | null;
  status: OrderStatus;
  dueDate: string | null;
  currency: string;
  total: string;
  itemCount: number;
  createdAt: string;
}

/** O pedido completo, com os itens. */
export interface Order extends OrderSummary {
  notes: string | null;
  items: OrderItem[];
  confirmedAt: string | null;
  deliveredAt: string | null;
  cancelledAt: string | null;
}
